import SelectMatchesDialogButton from "../Dashboard/Dialogs/SelectMatchesDialogButton";
import { Tournaments } from "@/api/tournaments";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/shadcn/select";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/shadcn/tooltip";

interface LeaderboardsNavigationProps {
    activeTab: "match" | "tournament" | "season";
    setActiveTab: (tab: "match" | "tournament" | "season") => void;
    selectedMetric?: "total" | "average" | "goals" | "assists" | "blockedShots";
    setSelectedMetric?: (metric: "total" | "average" | "goals" | "assists" | "blockedShots") => void;
    selectedTournament?: number;
    setSelectedTournament?: (tournamentId: number | undefined) => void;
    tournaments?: Tournaments;
    selectedMatches?: number[];
    setSelectedMatches?: (matchIds: number[]) => void;
    selectedSeason?: number;
    setSelectedSeason?: (season: number) => void;
}

const navItems = [
    { key: "match" as const, label: "Top Match Performances", shortLabel: "Match" },
    { key: "tournament" as const, label: "Tournament Champions", shortLabel: "Tournament" },
    { key: "season" as const, label: "Season Leaders", shortLabel: "Season" },
];

const currentYear = new Date().getFullYear();
const seasonYears = Array.from({ length: currentYear - 2022 + 1 }, (_, i) => currentYear - i);

const LeaderboardsNavigation = ({
    activeTab,
    setActiveTab,
    selectedTournament,
    setSelectedTournament,
    tournaments,
    selectedMatches,
    setSelectedMatches,
    selectedSeason,
    setSelectedSeason,
}: LeaderboardsNavigationProps) => {
    const handleTournamentSelect = (value: string) => {
        setSelectedTournament?.(value === "all" ? undefined : parseInt(value));
    };

    return (
        <div className="flex flex-col md:flex-row md:flex-wrap md:items-center md:justify-between gap-3 mb-3.5">
            <nav id="onboarding-leaderboards-nav" className="rounded-md p-1 bg-surface-container-low md:shrink-0">
                <div className="grid grid-cols-3 gap-1 md:flex md:items-center md:space-x-2 md:gap-0">
                    {navItems.map((item) => {
                        const isActive = activeTab === item.key;
                        return (
                            <button
                                key={item.key}
                                type="button"
                                className={`nav-tab ${isActive ? "nav-tab-active" : "nav-tab-inactive"}`}
                                onClick={() => {
                                    if (!isActive) setActiveTab(item.key);
                                }}
                            >
                                <span className="md:hidden">{item.shortLabel}</span>
                                <span className="hidden md:inline">{item.label}</span>
                            </button>
                        );
                    })}
                </div>
            </nav>
            <div id="onboarding-leaderboards-context-selector" className="md:shrink-0">
                {activeTab === "match" && setSelectedMatches && (
                    <div className="flex items-center justify-center gap-2">
                        <span className="text-sm text-on-surface-variant">
                            {selectedMatches && selectedMatches.length > 0
                                ? `${selectedMatches.length} match${selectedMatches.length > 1 ? "es" : ""}`
                                : "All matches"}
                        </span>
                        <SelectMatchesDialogButton
                            selectedMatchIds={selectedMatches || []}
                            onMatchSelect={(matchIds) => setSelectedMatches?.(matchIds)}
                            useExternalState={true}
                        />
                    </div>
                )}
                {activeTab === "tournament" && setSelectedTournament && tournaments && (
                    <TooltipProvider>
                        <Select
                            value={selectedTournament ? selectedTournament.toString() : "all"}
                            onValueChange={handleTournamentSelect}
                            disabled={!tournaments || tournaments.length === 0}
                        >
                            <SelectTrigger className="w-full md:w-fit text-sm">
                                <SelectValue placeholder="Select tournament" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Tournaments</SelectItem>
                                {tournaments.map((tournament) =>
                                    tournament.matches.length === 0 ? (
                                        <Tooltip key={tournament.id} delayDuration={0}>
                                            <TooltipTrigger asChild>
                                                <div
                                                    className="flex items-center px-2 py-1.5 text-sm opacity-50 cursor-not-allowed text-on-surface"
                                                    onClick={(e) => e.preventDefault()}
                                                >
                                                    <span>{tournament.name}</span>
                                                    <span className="ml-2 text-sm text-on-surface-variant">
                                                        {tournament.id}
                                                    </span>
                                                </div>
                                            </TooltipTrigger>
                                            <TooltipContent side="left">
                                                <p>No data for this tournament</p>
                                            </TooltipContent>
                                        </Tooltip>
                                    ) : (
                                        <SelectItem key={tournament.id} value={tournament.id.toString()}>
                                            <span>{tournament.name}</span>
                                            <span className="ml-2 text-sm text-on-surface-variant">
                                                {tournament.id}
                                            </span>
                                        </SelectItem>
                                    )
                                )}
                            </SelectContent>
                        </Select>
                    </TooltipProvider>
                )}
                {activeTab === "season" && setSelectedSeason && (
                    <Select
                        value={String(selectedSeason || currentYear)}
                        onValueChange={(v) => setSelectedSeason(parseInt(v))}
                    >
                        <SelectTrigger className="w-full md:w-fit text-sm">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {seasonYears.map((year) => (
                                <SelectItem key={year} value={String(year)}>
                                    {year}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                )}
            </div>
        </div>
    );
};

export default LeaderboardsNavigation;
